/* Expected-CTR curve, percentiles and the opportunity score.
   Extracted verbatim from artifact.html <script id="logic">.

   INVARIANT: expectedCtr() must stay strictly decreasing across positions 1..20.
   It drives both the CTR pill thresholds in the table and the "recoverable clicks"
   math on recover-type actions. */

export function expectedCtr(pos) {
  const base = { 1: 28, 2: 15, 3: 10, 4: 7.2, 5: 5.5, 6: 4.4, 7: 3.6, 8: 3.0, 9: 2.5, 10: 2.1 };
  return base[Math.round(pos)] || Math.max(0.4, 2.0 * Math.exp(-(pos - 10) / 6));
}

export function percentile(arr, p) {
  if (!arr.length) return 1;
  const a = arr.slice().sort((x, y) => x - y);
  return a[Math.min(a.length - 1, Math.floor(p * a.length))] || 1;
}

/* Writes `oscore` in place on conversational / question / uncertain rows. */
export function scoreOpportunities(rows) {
  const scored = rows.filter(d => ["conversational", "question", "uncertain"].includes(d.bucket));
  const p90 = Math.max(1, percentile(scored.map(d => d.imp), 0.9));
  for (const d of scored) {
    const impS = Math.min(d.imp / p90, 1) * 30;
    const posS = (Math.min(Math.max(d.pos - 1, 0), 11) / 11) * 25;
    const sigS = (d.cscore / 100) * 15;
    const exp = expectedCtr(d.pos);
    const ctrGap = Math.max(0, Math.min(1, (exp - d.ctr) / exp)) * 20;
    const gap = (d.imp >= 20 && d.clicks === 0) ? 10 : (d.clicks <= 1 ? 5 : 0);
    d.oscore = Math.round(impS + posS + sigS + ctrGap + gap);
  }
}

/* Opportunity band. Colour for each tone is mapped in the client. */
export function oppMeta(sc) {
  if (sc >= 85) return { label: "Critical", tone: "critical" };
  if (sc >= 70) return { label: "High", tone: "high" };
  if (sc >= 50) return { label: "Medium", tone: "medium" };
  if (sc >= 30) return { label: "Moderate", tone: "moderate" };
  return { label: "Low", tone: "low" };
}

/* Percentile scale used for the clicks/impressions colour coding in the query table. */
export function computeScales(rows) {
  const imps = rows.map(d => d.imp);
  const clks = rows.map(d => d.clicks);
  return {
    imp50: percentile(imps, 0.5),
    imp90: percentile(imps, 0.9),
    clk50: percentile(clks, 0.5),
    clk90: percentile(clks, 0.9),
  };
}

/* CTR health vs the expected curve: >=1 good, >=0.5 fair, <0.5 poor.
   Impressions below 10 are treated as too thin to judge. */
export function ctrHealth(d) {
  if (d.imp < 10) return { ratio: null, tone: "thin", expected: expectedCtr(d.pos) };
  const expected = expectedCtr(d.pos);
  const ratio = d.ctr / expected;
  return { ratio, expected, tone: ratio >= 1 ? "good" : ratio >= 0.5 ? "fair" : "poor" };
}
